import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarDay, faClock, faLocationDot,
    faCheck, faTimes, faSpinner, faDownload,
    faChevronDown, faChevronUp, faCrown
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';
import Logo from '../assets/imges.png';

const API_INVITATION = `${API_CONFIG.apiUrl}api_invitation.php`;

// ============================================================
// HOOK : Particules "Luciole" (or doux sur fond clair)
// ============================================================
const useParticles = (canvasRef, containerRef) => {
    const particlesRef = useRef([]);
    const animationRef = useRef(null);
    const dimensionsRef = useRef({ width: 0, height: 0 });
    const isMobile = useRef(window.innerWidth < 640);

    const initParticles = useCallback((width, height) => {
        const count = isMobile.current ? 15 : 35;
        const particles = [];
        for (let i = 0; i < count; i++) {
            particles.push({
                x: Math.random() * width,
                y: Math.random() * height,
                vx: (Math.random() - 0.5) * 0.15,
                vy: (Math.random() - 0.5) * 0.15,
                radius: Math.random() * 1.8 + 0.5,
                opacity: Math.random() * 0.3 + 0.1,
            });
        }
        return particles;
    }, []);

    const draw = useCallback((ctx, width, height, particles) => {
        ctx.clearRect(0, 0, width, height);
        for (const p of particles) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(212, 175, 55, ${p.opacity})`;
            ctx.shadowBlur = 15;
            ctx.shadowColor = "rgba(212, 175, 55, 0.4)";
            ctx.fill();
        }
    }, []);

    const animate = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ctx = canvas.getContext('2d');
        const rect = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const width = rect.width;
        const height = rect.height;

        if (dimensionsRef.current.width !== width || dimensionsRef.current.height !== height) {
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            ctx.scale(dpr, dpr);
            dimensionsRef.current.width = width;
            dimensionsRef.current.height = height;
            particlesRef.current = initParticles(width, height);
        }

        const particles = particlesRef.current;
        const now = Date.now();
        for (const p of particles) {
            p.x += p.vx;
            p.y += p.vy;
            if (p.x < -20) p.x = width + 20;
            if (p.x > width + 20) p.x = -20;
            if (p.y < -20) p.y = height + 20;
            if (p.y > height + 20) p.y = -20;
            p.opacity = (Math.sin(now / 1800 + p.x) + 1) / 2 * 0.4 + 0.1;
        }

        draw(ctx, width, height, particles);
        animationRef.current = requestAnimationFrame(animate);
    }, [canvasRef, containerRef, initParticles, draw]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        animate();
        return () => { if (animationRef.current) cancelAnimationFrame(animationRef.current); };
    }, [animate, containerRef]);
};

// ============================================================
// COMPOSANT InvitationDown - PDF avec Logo, Description & QR Code
// ============================================================
const InvitationDown = ({ event, invitation, inviteUrl }) => {
    const [loading, setLoading] = useState(false);
    const [downloadStatus, setDownloadStatus] = useState('');

    const formatDate = (dateStr) => {
        if (!dateStr) return "Date à définir";
        return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    };

    const loadImage = (url) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = url;
        });
    };

    const handleDownload = async () => {
        if (loading) return;
        setLoading(true);
        setDownloadStatus('Préparation du Pass...');

        try {
            const [logoDataUrl, qrDataUrl] = await Promise.all([
                loadImage(Logo),
                loadImage(`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(inviteUrl)}`)
            ]);

            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageWidth = 210;
            const pageHeight = 297;

            pdf.setFillColor(248, 246, 240);
            pdf.rect(0, 0, pageWidth, pageHeight, 'F');

            pdf.setDrawColor(212, 175, 55);
            pdf.setLineWidth(0.5);
            pdf.rect(15, 15, pageWidth - 30, pageHeight - 30, 'S');
            pdf.setLineWidth(0.1);
            pdf.rect(17, 17, pageWidth - 34, pageHeight - 34, 'S');

            const logoSize = 45;
            pdf.addImage(logoDataUrl, 'PNG', (pageWidth - logoSize) / 2, 20, logoSize, logoSize);

            let y = 20 + logoSize + 15;
            pdf.setFont('times', 'bold');
            pdf.setFontSize(26);
            pdf.setTextColor(139, 0, 0);
            const titleLines = pdf.splitTextToSize((event.titres || "").toUpperCase(), pageWidth - 40);
            pdf.text(titleLines, pageWidth / 2, y, { align: 'center' });
            y += (titleLines.length * 10) + 12;

            if (event.description) {
                pdf.setFont('helvetica', 'italic');
                pdf.setFontSize(12);
                pdf.setTextColor(80, 80, 80);
                const descLines = pdf.splitTextToSize(event.description, pageWidth - 50);
                pdf.text(descLines, pageWidth / 2, y, { align: 'center' });
                y += (descLines.length * 7) + 15;
            }

            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(11);
            pdf.setTextColor(100, 100, 100);
            pdf.text('Cette invitation est strictement personnelle et accordée à', pageWidth / 2, y, { align: 'center' });
            y += 10;
            
            pdf.setFont('times', 'bold');
            pdf.setFontSize(22);
            pdf.setTextColor(212, 175, 55);
            const guestName = `${invitation.prenom || ''} ${invitation.nom || ''}`.toUpperCase();
            pdf.text(guestName, pageWidth / 2, y, { align: 'center' });
            y += 11;

            pdf.setDrawColor(212, 175, 55);
            pdf.line(pageWidth / 2 - 30, y, pageWidth / 2 + 30, y);
            y += 20;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            pdf.setTextColor(139, 0, 0);
            pdf.text('QUAND', pageWidth / 2, y, { align: 'center', charSpace: 1 });
            y += 6;
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(50, 50, 50);
            pdf.text(`${formatDate(event.dates)} à ${event.heures ? event.heures.substring(0,5) : ''}`, pageWidth / 2, y, { align: 'center' });
            y += 14;

            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(139, 0, 0);
            pdf.text('OÙ', pageWidth / 2, y, { align: 'center', charSpace: 1 });
            y += 6;
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(50, 50, 50);
            const addressLines = pdf.splitTextToSize(event.adresse || "", pageWidth - 60);
            pdf.text(addressLines, pageWidth / 2, y, { align: 'center' });

            const qrSize = 30;
            pdf.addImage(qrDataUrl, 'PNG', (pageWidth - qrSize) / 6, pageHeight - 60, qrSize, qrSize);

            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(8);
            pdf.setTextColor(150, 150, 150);
            pdf.text('Scannez ce code à l\'entrée', pageWidth / 5, pageHeight - 25, { align: 'center' });

            pdf.save(`VIP_Pass_${invitation.nom || 'Invite'}.pdf`);
            setDownloadStatus('Pass téléchargé');
            setTimeout(() => setDownloadStatus(''), 3000);
        } catch (error) {
            console.error(error);
            setDownloadStatus('Erreur');
            setTimeout(() => setDownloadStatus(''), 3000);
        } finally {
            setLoading(false);
        }
    };

    return (
        <button className="btn-download-vip" onClick={handleDownload} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faDownload} />}
            <span>{loading ? downloadStatus : 'Télécharger mon Pass VIP'}</span>
        </button>
    );
};

// ============================================================
// COMPOSANT PRINCIPAL
// ============================================================
const PublicInvitationResponse = () => {
    const { id_event, id } = useParams();
    const containerRef = useRef(null);
    const canvasRef = useRef(null);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [fadeIn, setFadeIn] = useState(false);
    const [orgOpen, setOrgOpen] = useState(false);

    useParticles(canvasRef, containerRef);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await fetch(`${API_INVITATION}?action=detail&id_event=${id_event}&id=${id}`);
                const result = await res.json();
                if (result.status === 'success') {
                    setData(result.data);
                    setTimeout(() => setFadeIn(true), 150); // Délai pour l'animation d'entrée
                } else setError(result.message || 'Introuvable');
            } catch (err) { setError(err.message); }
            finally { setLoading(false); }
        };
        fetchData();
    }, [id_event, id]);

    const updateStatus = async (newStatus) => {
        if (updating) return;
        setUpdating(true);
        try {
            const formData = new FormData();
            formData.append('status', newStatus);
            const res = await fetch(`${API_INVITATION}?action=update&id=${id}`, { method: 'POST', body: formData });
            const result = await res.json();
            if (result.status === 'success') {
                setData((prev) => ({ ...prev, invitation: { ...prev.invitation, status: newStatus } }));
            }
        } catch (err) { console.error(err); }
        finally { setUpdating(false); }
    };

    const inviteUrl = window.location.href;

    if (loading) return (
        <div className="white-vip-root" ref={containerRef}>
            <canvas ref={canvasRef} className="light-particles" />
            <div className="loader-glare"></div>
        </div>
    );

    if (error || !data) return (
        <div className="white-vip-root">
            <div className="error-box">
                <FontAwesomeIcon icon={faTimes} className="error-icon" />
                <h2>Accès Refusé</h2>
                <p>Invitation introuvable ou expirée.</p>
            </div>
        </div>
    );

    const { event, invitation } = data;
    const isConfirmed = invitation.status === 'acceptée' || invitation.status === 'rejetée';

    return (
        <div className="white-vip-root" ref={containerRef}>
            <div className="bg-glow-light"></div>
            <canvas ref={canvasRef} className="light-particles" />

            <div className={`vip-container ${fadeIn ? 'visible' : ''}`}>
                <div className="white-card">
                    {/* Cadre intérieur doré style impression */}
                    <div className="inner-gold-border"></div>

                    {/* Header */}
                    <div className="white-header stagger-1">
                        <div className="logo-wrapper">
                            <img src={Logo} alt="Logo de l'événement" className="main-header-logo" />
                        </div>
                        <span className="badge-vip">
                            <FontAwesomeIcon icon={faCrown} className="icon-crown"/> VIP GUEST
                        </span>
                        <h1 className="title-cormorant">{event.titres}</h1>
                        <div className="divider-ornament">
                            <span></span><div className="diamond"></div><span></span>
                        </div>
                    </div>

                    <div className="white-body">
                        {/* Section Image / Infos */}
                        <div className="event-info-panel stagger-2">
                            {event.images && (
                                <div className="img-container">
                                    <img src={`${API_CONFIG.apiUrl}${event.images}`} alt="Event" />
                                    <div className="img-overlay-light"></div>
                                </div>
                            )}

                            <div className="info-list">
                                <div className="info-item">
                                    <div className="icon-box"><FontAwesomeIcon icon={faCalendarDay} /></div>
                                    <p>{new Date(event.dates).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                </div>
                                <div className="info-item">
                                    <div className="icon-box"><FontAwesomeIcon icon={faClock} /></div>
                                    <p>{event.heures}</p>
                                </div>
                                <div className="info-item">
                                    <div className="icon-box"><FontAwesomeIcon icon={faLocationDot} /></div>
                                    <p>{event.adresse}</p>
                                </div>
                            </div>

                            {event.description && (
                                <p className="event-desc">"{event.description}"</p>
                            )}
                        </div>

                        {/* Section Guest & Action */}
                        <div className="guest-action-panel stagger-3">
                            <div className="guest-id-card">
                                <p className="label-tiny">INVITATION DÉCERNÉE EXCLUSIVEMENT À</p>
                                <h2 className="guest-fullname">{invitation.prenom} {invitation.nom}</h2>
                                <p className="guest-contact">{invitation.telephone}</p>
                            </div>

                            {!isConfirmed ? (
                                <div className="decision-box">
                                    <p className="decision-title">Nous serions honorés de votre présence.</p>
                                    <div className="btn-group">
                                        <button className="btn-red-gold" onClick={() => updateStatus('acceptée')} disabled={updating}>
                                            {updating ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Accepter l\'invitation'}
                                        </button>
                                        <button className="btn-outline-red" onClick={() => updateStatus('rejetée')} disabled={updating}>
                                            {updating ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Décliner respectueusement'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="result-box">
                                    {invitation.status === 'acceptée' ? (
                                        <>
                                            <div className="success-mark"><FontAwesomeIcon icon={faCheck} /></div>
                                            <p className="result-text">Votre place est réservée avec succès.</p>
                                            <InvitationDown event={event} invitation={invitation} inviteUrl={inviteUrl} />
                                        </>
                                    ) : (
                                        <p className="result-text declined">Vous avez décliné cette invitation. Nous espérons vous revoir bientôt.</p>
                                    )}
                                </div>
                            )}

                            {/* Organisation Accordion */}
                            <div className="org-wrapper">
                                <div className="org-head" onClick={() => setOrgOpen(!orgOpen)}>
                                    <img src={Logo} alt="Logo Organisation" className="org-logo-small" />
                                    <span>L'Organisation</span>
                                    <FontAwesomeIcon icon={orgOpen ? faChevronUp : faChevronDown} className="arrow" />
                                </div>
                                <div className={`org-body ${orgOpen ? 'expanded' : ''}`}>
                                    <p><strong>Vision :</strong> Émergence d'une société responsable et engagée.</p>
                                    <p><strong>Mission :</strong> Accompagnement au développement économique et social.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* ============================================================
                STYLES - BLANC, ROUGE & OR (Luxe Ultime)
                ============================================================ */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');

                :root {
                    --bg-white: #F9F8F6;
                    --white-card: rgba(255, 255, 255, 0.97);
                    --red-deep: #780000;
                    --red-light: #9B111E;
                    --gold-primary: #D4AF37;
                    --gold-light: #F3E5AB;
                    --gold-glow: rgba(212, 175, 55, 0.35);
                    --text-dark: #2C2C2C;
                    --text-muted: #737373;
                    --font-serif: 'Cormorant Garamond', serif;
                    --font-sans: 'Jost', sans-serif;
                }

                * { box-sizing: border-box; margin: 0; padding: 0; }

                /* --- FOND ET LAYOUT GLOBAL --- */
                .white-vip-root {
                    position: relative;
                    min-height: 100vh;
                    width: 100%;
                    background-color: var(--bg-white);
                    background-image: radial-gradient(circle at 50% -20%, #FFFDF8 0%, var(--bg-white) 70%, #EFEBE1 100%);
                    display: flex; align-items: center; justify-content: center;
                    padding: 60px 20px;
                    font-family: var(--font-sans);
                    color: var(--text-dark);
                    overflow-x: hidden;
                }

                .bg-glow-light {
                    position: absolute; top: 10%; left: 50%; transform: translateX(-50%);
                    width: 70vw; height: 70vh;
                    background: radial-gradient(circle, rgba(212,175,55,0.06) 0%, transparent 60%);
                    filter: blur(80px); z-index: 0; pointer-events: none;
                }

                .light-particles {
                    position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 0; pointer-events: none;
                }

                /* --- LOADER --- */
                .loader-glare {
                    width: 60px; height: 60px; border-radius: 50%;
                    border: 2px solid rgba(212, 175, 55, 0.2); border-top-color: var(--gold-primary);
                    animation: spin 1s linear infinite; z-index: 10;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* --- ANIMATIONS D'ENTRÉE --- */
                @keyframes slideFadeUp {
                    0% { opacity: 0; transform: translateY(30px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                .vip-container {
                    position: relative; z-index: 1; width: 100%; max-width: 1100px;
                    opacity: 0; transform: translateY(40px);
                    transition: opacity 1.2s cubic-bezier(0.2, 0.8, 0.2, 1), transform 1.2s cubic-bezier(0.2, 0.8, 0.2, 1);
                }
                .vip-container.visible { opacity: 1; transform: translateY(0); }

                /* Stagger children inside the visible container */
                .vip-container.visible .stagger-1 { animation: slideFadeUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0.2s forwards; opacity: 0; }
                .vip-container.visible .stagger-2 { animation: slideFadeUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0.4s forwards; opacity: 0; }
                .vip-container.visible .stagger-3 { animation: slideFadeUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) 0.5s forwards; opacity: 0; }

                /* --- LA CARTE --- */
                .white-card {
                    background: var(--white-card);
                    backdrop-filter: blur(20px);
                    border-radius: 20px;
                    box-shadow: 0 25px 60px rgba(0,0,0,0.04), 0 10px 25px rgba(212, 175, 55, 0.05);
                    position: relative;
                    overflow: hidden;
                }

                /* Bordure intérieure dorée (effet impression de luxe) */
                .inner-gold-border {
                    position: absolute;
                    inset: 12px;
                    border: 1px solid rgba(212, 175, 55, 0.25);
                    border-radius: 12px;
                    pointer-events: none;
                    z-index: 10;
                }

                /* --- HEADER --- */
                .white-header {
                    padding: 50px 40px 30px;
                    text-align: center;
                    display: flex; flex-direction: column; align-items: center;
                    position: relative; z-index: 2;
                }

                .logo-wrapper {
                    width: 110px; height: 110px;
                    border-radius: 50%;
                    background: #FFFFFF;
                    border: 1px solid var(--gold-light);
                    box-shadow: 0 10px 25px rgba(212, 175, 55, 0.15), inset 0 0 15px rgba(0,0,0,0.02);
                    display: flex; align-items: center; justify-content: center;
                    margin-bottom: 25px; padding: 20px;
                    position: relative;
                }
                .logo-wrapper::before {
                    content: ''; position: absolute; inset: -5px;
                    border-radius: 50%; border: 1px dashed rgba(212, 175, 55, 0.4);
                    animation: spin 20s linear infinite;
                }
                .main-header-logo { width: 100%; height: auto; object-fit: contain; }

                .badge-vip {
                    display: inline-flex; align-items: center; gap: 8px;
                    font-size: 0.75rem; font-weight: 600; letter-spacing: 5px;
                    color: var(--red-deep);
                    text-transform: uppercase;
                    margin-bottom: 20px;
                }
                .icon-crown { color: var(--gold-primary); font-size: 1rem; }

                .title-cormorant {
                    font-family: var(--font-serif);
                    font-size: clamp(2.2rem, 4vw, 3.8rem);
                    font-weight: 400;
                    letter-spacing: 1px;
                    line-height: 1.2;
                    color: var(--red-deep);
                    margin-bottom: 24px;
                }

                .divider-ornament {
                    display: flex; align-items: center; justify-content: center; gap: 15px;
                    width: 150px; margin: 0 auto;
                }
                .divider-ornament span { flex: 1; height: 1px; background: rgba(212, 175, 55, 0.5); }
                .divider-ornament .diamond { width: 6px; height: 6px; background: var(--gold-primary); transform: rotate(45deg); }

                /* --- CORPS DE LA CARTE --- */
                .white-body {
                    display: grid; grid-template-columns: 1fr 1fr; gap: 50px;
                    padding: 10px 60px 60px; position: relative; z-index: 2;
                }

                /* Section Gauche */
                .img-container {
                    width: 100%; height: 260px;
                    border-radius: 12px;
                    overflow: hidden; margin-bottom: 30px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.08);
                    position: relative;
                }
                .img-container img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.5s ease; }
                .img-container:hover img { transform: scale(1.05); }
                .img-overlay-light {
                    position: absolute; inset: 0;
                    background: linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%);
                }

                .info-list { display: flex; flex-direction: column; gap: 12px; margin-bottom: 30px; }
                .info-item {
                    display: flex; align-items: center; gap: 15px;
                    padding: 12px 15px;
                    border-bottom: 1px solid rgba(212, 175, 55, 0.15);
                }
                .icon-box {
                    width: 36px; height: 36px;
                    background: rgba(212, 175, 55, 0.08);
                    border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--gold-primary); font-size: 1rem;
                }
                .info-item p { font-size: 1.05rem; color: var(--text-dark); font-weight: 400; }

                .event-desc {
                    font-family: var(--font-serif); font-size: 1.3rem; font-style: italic;
                    color: var(--text-muted); line-height: 1.6; text-align: center;
                    padding: 0 15px;
                }

                /* Section Droite */
                .guest-action-panel { display: flex; flex-direction: column; gap: 35px; }

                .guest-id-card {
                    background: #FFFFFF;
                    padding: 35px 25px;
                    border-radius: 12px;
                    border: 1px solid rgba(212, 175, 55, 0.2);
                    text-align: center;
                    box-shadow: 0 10px 20px rgba(0,0,0,0.02);
                }
                .label-tiny {
                    font-size: 0.65rem; color: var(--text-muted); letter-spacing: 3px;
                    margin-bottom: 15px; text-transform: uppercase; font-weight: 500;
                }
                .guest-fullname {
                    font-family: var(--font-serif); font-size: 2.4rem; font-weight: 500;
                    color: var(--text-dark); margin-bottom: 8px; letter-spacing: 1px;
                }
                .guest-contact { font-size: 0.9rem; color: var(--gold-primary); font-weight: 400; letter-spacing: 1px; }

                /* Boutons d'action */
                .decision-box { text-align: center; }
                .decision-title {
                    font-family: var(--font-serif); font-size: 1.3rem; font-style: italic;
                    margin-bottom: 20px; color: var(--text-muted);
                }
                .btn-group { display: flex; flex-direction: column; gap: 15px; }

                .btn-red-gold {
                    background: linear-gradient(135deg, var(--red-deep) 0%, var(--red-light) 100%);
                    color: #fff;
                    border: 1px solid rgba(255,255,255,0.2);
                    padding: 18px; border-radius: 8px;
                    font-family: var(--font-sans); font-size: 0.95rem; font-weight: 500;
                    text-transform: uppercase; letter-spacing: 2px;
                    cursor: pointer; transition: all 0.4s ease;
                    box-shadow: 0 10px 20px rgba(120, 0, 0, 0.2);
                    position: relative; overflow: hidden;
                }
                .btn-red-gold::after {
                    content: ''; position: absolute; top: 0; left: -100%; width: 50%; height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
                    transition: 0.5s;
                }
                .btn-red-gold:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 15px 25px rgba(120, 0, 0, 0.3); }
                .btn-red-gold:hover::after { left: 100%; }

                .btn-outline-red {
                    background: transparent; color: var(--text-muted);
                    border: 1px solid rgba(115,115,115,0.3);
                    padding: 16px; border-radius: 8px;
                    font-family: var(--font-sans); font-size: 0.85rem; text-transform: uppercase;
                    letter-spacing: 2px; cursor: pointer; transition: all 0.3s ease;
                }
                .btn-outline-red:hover:not(:disabled) { border-color: var(--red-deep); color: var(--red-deep); }

                /* Résultat / Download */
                .result-box {
                    text-align: center; padding: 30px;
                    background: linear-gradient(180deg, #FFFFFF, #FCFAF5);
                    border-radius: 12px; border: 1px solid rgba(212, 175, 55, 0.2);
                }
                .success-mark {
                    width: 60px; height: 60px; margin: 0 auto 15px;
                    background: rgba(212,175,55,0.1); border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.8rem; color: var(--gold-primary);
                }
                .result-text { font-size: 1.1rem; color: var(--text-dark); margin-bottom: 25px; }
                .result-text.declined { color: var(--text-muted); font-style: italic; }

                .btn-download-vip {
                    background: var(--text-dark); color: var(--gold-primary);
                    border: none; padding: 16px 24px; border-radius: 8px;
                    font-family: var(--font-sans); font-size: 0.9rem; font-weight: 500;
                    letter-spacing: 1px; cursor: pointer; width: 100%;
                    display: inline-flex; align-items: center; justify-content: center; gap: 10px;
                    transition: all 0.3s ease; box-shadow: 0 8px 20px rgba(0,0,0,0.15);
                }
                .btn-download-vip:hover { background: #1A1A1A; transform: translateY(-2px); box-shadow: 0 12px 25px rgba(212, 175, 55, 0.2); }

                /* Organisation */
                .org-wrapper {
                    border-top: 1px solid rgba(212, 175, 55, 0.2); padding-top: 25px; margin-top: auto;
                }
                .org-head {
                    display: flex; align-items: center; gap: 15px; justify-content: center;
                    cursor: pointer; color: var(--text-muted); transition: color 0.3s;
                }
                .org-head:hover { color: var(--red-deep); }
                .org-logo-small { width: 30px; height: 30px; object-fit: contain; }
                .org-head span { font-weight: 500; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 2px; }
                .org-body { max-height: 0; overflow: hidden; opacity: 0; transition: all 0.4s ease; font-size: 0.9rem; line-height: 1.7; text-align: center; }
                .org-body.expanded { max-height: 200px; opacity: 1; margin-top: 20px; color: var(--text-muted); }
                .org-body strong { color: var(--text-dark); font-weight: 500; }

                /* --- RESPONSIVE --- */
                @media (max-width: 900px) {
                    .white-body { grid-template-columns: 1fr; padding: 10px 40px 40px; gap: 40px; }
                }
                @media (max-width: 600px) {
                    .white-vip-root { padding: 20px 10px; }
                    .inner-gold-border { inset: 8px; } /* Ajustement pour mobile */
                    .white-header { padding: 40px 20px 20px; }
                    .logo-wrapper { width: 90px; height: 90px; padding: 15px; }
                    .title-cormorant { font-size: 2rem; }
                    .white-body { padding: 10px 20px 30px; }
                    .guest-id-card { padding: 25px 15px; }
                    .guest-fullname { font-size: 2rem; }
                    .btn-red-gold { font-size: 0.85rem; padding: 16px; }
                }
            `}</style>
        </div>
    );
};

export default PublicInvitationResponse;